/**
 * Audio utility functions for the podcast insights app
 */

/**
 * Formats seconds into a HH:MM:SS or MM:SS string display format
 * @param time - Time in seconds
 * @returns Formatted time string (HH:MM:SS for > 60 minutes, MM:SS otherwise)
 */
export const formatTime = (time: number): string => {
  if (!isFinite(time) || isNaN(time) || time < 0) return "0:00";
  
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Parses a timestamp string (MM:SS) into seconds
 * @param timestamp - Timestamp string in MM:SS format
 * @returns Time in seconds
 */
export const parseTimestamp = (timestamp: string): number => {
  const parts = timestamp.split(':');
  if (parts.length !== 2) return 0;
  
  const minutes = parseFloat(parts[0]) || 0;
  const seconds = parseFloat(parts[1]) || 0;
  
  return minutes * 60 + seconds;
};

/**
 * Parses a timestamp range (MM:SS - MM:SS) into start and end seconds
 * @param timestampRange - Timestamp range string (MM:SS - MM:SS)
 * @returns Object containing start and end times in seconds
 */
export const parseTimestampRange = (timestampRange: string): { start: number; end: number } => {
  if (!timestampRange || typeof timestampRange !== 'string') {
    return { start: 0, end: 0 };
  }

  try {
    const [startStr, endStr] = timestampRange.split(' - ');
    if (!startStr || !endStr) {
      return { start: 0, end: 0 };
    }
    return {
      start: parseTimestamp(startStr),
      end: parseTimestamp(endStr)
    };
  } catch (error) {
    console.warn('Error parsing timestamp range:', error);
    return { start: 0, end: 0 };
  }
}; 